import {
  Component,
  effect,
  input,
  OnDestroy,
  OnInit,
  signal,
  ChangeDetectionStrategy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatSlider, MatSliderThumb } from '@angular/material/slider';
import { MatSlideToggle } from '@angular/material/slide-toggle';
import { TranslatePipe } from '@ngx-translate/core';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faHeadset } from '@fortawesome/pro-duotone-svg-icons';
import { AppStore } from '../../../../state/app.repository';
import { AppService } from '../../../../core/services/app.service';
import { MediaConfigStore } from '../../../../state/media-config.repository';
import { UserSettingsStore } from '../../../../state/user-settings.repository';
import { SelectField } from '../../../../shared/components/form-fields/select-field/select-field';
import { MediaAccessPrompt } from '../../../../shared/components/ui/media-access-prompt/media-access-prompt';
import { MediaAccessDenied } from '../../../../shared/components/ui/media-access-denied/media-access-denied';
import { MatSliderWrapper } from '../../../../shared/components/material-wrappers/mat-slider-wrapper/mat-slider-wrapper';
import { MatToggleWrapper } from '../../../../shared/components/material-wrappers/mat-toggle-wrapper/mat-toggle-wrapper';
import { Button } from '../../../../shared/components/ui/buttons/gradient-btn/button';
import { DeviceService } from '../../../../core/services/device.service';
import { MediaPermissionsService } from '../../../../core/services/media-permissions.service';
import { error, info, isFirefoxBrowser } from '@4players/odin-common';
import { OdinService } from '../../../../core/services/odin.service';
import { config } from '../../../../../environments/environment';
import { getPercentage } from '../../../../shared/helpers/number';

@Component({
  selector: 'andor-settings-audio',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    FontAwesomeModule,
    TranslatePipe,
    MatSlideToggle,
    MatSlider,
    MatSliderThumb,
    Button,
    SelectField,
    MatSliderWrapper,
    MatToggleWrapper,
    MediaAccessPrompt,
    MediaAccessDenied,
  ],
  templateUrl: './settings-audio.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrl: './settings-audio.scss',
})
export class SettingsAudio implements OnInit, OnDestroy {
  protected readonly faHeadset = faHeadset;
  mode = input<'simple' | 'full'>('full');
  inputVolume = signal(1);
  outputVolume = signal(1);
  echoCancellation = signal(true);
  autoGainControl = signal(true);
  voiceActivityDetection = signal(true);
  noiseSuppression = signal(true);
  voiceIsolation = signal(false);
  voiceIsolationAttenuationLimit = signal(0);
  voiceIsolationPostFilterBeta = signal(0.0);
  volumeGate = signal(-35);
  volumeGatePercent = signal(0);
  volumeGateMin = -90;
  volumeGateMax = -1;
  selectFieldDirection: 'top' | 'bottom' = 'top';

  constructor(
    public appStore: AppStore,
    public appService: AppService,
    public deviceService: DeviceService,
    public permissions: MediaPermissionsService,
    private _mediaConfig: MediaConfigStore,
    private _userSettings: UserSettingsStore,
    private _odinService: OdinService
  ) {
    if (appStore.value.activeSection === 'general-settings') {
      this.selectFieldDirection = 'bottom';
    }
    this.outputVolume.set(_userSettings.value.roomVolume);
    this.inputVolume.set(_userSettings.value.inputVolume);
    this.volumeGate.set(_userSettings.value.volumeGate);
    this.echoCancellation.set(_mediaConfig.value.echoCancellation);
    this.autoGainControl.set(_mediaConfig.value.autoGainControl);
    this.voiceActivityDetection.set(_userSettings.value.voiceActivityDetection);
    this.noiseSuppression.set(_mediaConfig.value.noiseSuppression);

    effect(() => {
      const status = permissions.microphoneAccess();
      if (status === 'granted') {
        if (isFirefoxBrowser()) {
          permissions.requestUserMedia('audio').catch(error);
        }
        deviceService.refreshAudioInputDevices().then();
        deviceService.refreshAudioOutputDevices().then();
      }
    });
  }

  private releaseLevelPolling?: () => void;

  get showTestComponents(): boolean {
    return config.showTestComponents;
  }

  async ngOnInit() {
    this.releaseLevelPolling = this._odinService.holdLevelPolling();
    this._odinService.audioInput$.subscribe((audioInput) => {
      if (audioInput) {
        audioInput.addEventListener('PowerLevel', (event) => {
          this.volumeGatePercent.set(
            getPercentage(event.payload.media.powerLevel, this.volumeGateMin, this.volumeGateMax)
          );
        });
      }
    });

    if (this.permissions.microphoneAccess() === 'granted' && !this._odinService.room) {
      info(`ensuring audio input for audio settings ...`);
      await this._odinService.ensureAudioInputExists();
    }
  }

  ngOnDestroy() {
    this.releaseLevelPolling?.();
    // AudioInput lifecycle is owned by OdinService's reconcile subscription —
    // mutedState drives mute/unmute, so no explicit teardown here.
  }

  outputVolumeChanged(volume: number) {
    this._userSettings.update({ roomVolume: volume });
  }

  inputVolumeChanged(volume: number) {
    this._userSettings.update({ inputVolume: volume });
  }

  updateVad() {
    this._userSettings.store.update((state) => ({
      ...state,
      voiceActivityDetection: !state.voiceActivityDetection,
    }));
  }

  updateEC() {
    this._mediaConfig.store.update((state) => ({
      ...state,
      echoCancellation: !state.echoCancellation,
    }));
  }

  updateAGC() {
    this._mediaConfig.store.update((state) => ({
      ...state,
      autoGainControl: !state.autoGainControl,
    }));
  }

  updateNoiseSuppression() {
    this._mediaConfig.store.update((state) => ({
      ...state,
      noiseSuppression: !state.noiseSuppression,
    }));
  }

  updateVolumeGate(value: number) {
    this._userSettings.store.update((state) => ({
      ...state,
      volumeGate: value,
    }));
  }

  onVoiceIsolationChange() {
    this.voiceIsolation.set(!this.voiceIsolation());
    this.updateVoiceIsolation();
  }

  onVoiceIsolationAttenuationLimitChange(value: number) {
    this.voiceIsolationAttenuationLimit.set(value);
    this.updateVoiceIsolation();
  }

  onVoiceIsolationPostFilterBetaChange(value: number) {
    throw new Error('Method not implemented.');
  }

  updateVoiceIsolation() {
    throw new Error('Method not implemented.');
  }

  formatVolume(value: number): string {
    return `${Math.round(value * 100)}%`;
  }

  formatDBFS(value: number): string {
    return `${value}`;
  }

  async requestMic() {
    await this.permissions.requestMic();
    await this.deviceService.refreshAudioInputDevices();
  }
}
